import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import UsersDropdown from 'common/users/containers/users-dropdown-container';
import { Modal, Icon, Button, Flex, Div } from 'ui';
import Table from 'common/table/legacy-table';
import { Row, Col, Popover, Select } from 'antd';
import { StyledInfoIcon, StyledRoleDropdown, StyledButton } from './user-management-table';

const { Option } = Select;

const StyledIcon = styled(Icon)`
  color: white;
`;

const StyledUsersDropdown = styled(UsersDropdown)`
  width: 100%;
`;

const StyledLabel = styled.p`
  margin: 10px 0 10px 10px;
`;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    width: 630px
  }
`;

const CloseButton = styled(Button)`
  float: right;
  margin-top: 20px;
  text-transform: uppercase;
  color: ${p => p.theme.colors.darkThemeSecondary};
  border-color: ${p => p.theme.colors.darkThemeSecondary};
  &:hover {
    border-color: ${p => p.theme.colors.darkThemeSecondary};
    color: ${p => p.theme.colors.darkThemeSecondary};
    opacity: 0.7;
  }
  &:focus {
    background-color: ${p => p.theme.colors.darkThemeSecondary};
    color: ${p => p.theme.colors.darkThemeSecondary};
  }
`;

const StyledAddUsersButton = styled(Button)`
  text-transform: uppercase;
  width: 100%;
  color: white;
  height: 32px;
  background-color: ${p => p.theme.colors.theme} !important;
  &:hover {
    opacity: 0.7;
    color: white;
    background-color: ${p => p.theme.colors.theme} !important;
  }
  &:focus {
    color: white;
    background-color: ${p => p.theme.colors.theme} !important;
  }
`;

const StyledSpan = styled.span`
  text-transform: uppercase;
`;
const AddUsersModal = ({
  intl,
  visible,
  onClose,
  activeServiceCenter,
  roles,
  handleUpdate,
  serviceCenters,
  handleDelete,
  handleAdd,
  latestAddedUsers,
  isAdding,
  tooltipText,
  isDeleting,
  isUpdating,
}) => {
  const [dropdownValue, setDropdownValue] = useState('');
  const [selectedUser, setSelectedUser] = useState();

  useEffect(() => {
    if (visible) {
      setDropdownValue('');
      setSelectedUser();
    }
  }, [visible]);

  const getTitle = () =>
    <>
      <StyledIcon type='user-plus' />
      {' '}
      <StyledSpan>
        {intl.formatMessage({ id: 'userManagement.ADD_USER' })}
      </StyledSpan>
    </>;

  const handleDeleteButtonClick = (e, id) => {
    e.stopPropagation();
    setDropdownValue('');
    setSelectedUser();
    handleDelete(id, true);
  };

  const getColumns = () => [
    {
      label: '',
      dataIndex: 'id',
      renderValueFn: id =>
        <StyledButton
          onClick={e => handleDeleteButtonClick(e, id)}
          shape='circle'
        >
          <StyledIcon type='trash-alt' />
        </StyledButton>,
      width: '40px',
    },
    {
      label: 'Name',
      dataIndex: 'displayName',
      width: '240px',
    },
    {
      label: 'Access rights',
      dataIndex: 'role',
      renderValueFn: (role, row) =>
        <Flex jcfs>
          <StyledRoleDropdown
            dropdownMatchSelectWidth={false}
            disabled={isDeleting || isUpdating}
            value={row.role.id}
            onSelect={newRole => handleUpdate(row.id, newRole)}
          >
            {roles
              ?.filter(r => r.isAssignable)
              ?.map(r => <Option key={r.id} value={r.id}>
                {r.description}
              </Option>)}
          </StyledRoleDropdown>
          <Popover
            placement='bottomLeft'
            title={tooltipText}
            overlayClassName='permissions-tooltip'
          >
            <div>
              <StyledInfoIcon type='info-circle' onClick={e => e.preventDefault()} />
            </div>
          </Popover>
        </Flex>,
      width: '230px',
    },
  ];

  const onAddUser = () => {
    handleAdd(selectedUser?.value);
    setDropdownValue('');
  };

  const handleSelect = (selected) => {
    setSelectedUser(selected);
    setDropdownValue(selected.selectedUser.displayName);
  };

  return (
    <StyledModal
      title={getTitle()}
      open={visible}
      footer={null}
      onCancel={onClose}
    >
      <Row>
        <Col span={24}>
          <StyledLabel>{intl.formatMessage({ id: 'userManagement.ADD_INSTRUCTION' })}</StyledLabel>
        </Col>
      </Row>
      <Row>
        <Col span={20}>
          <StyledUsersDropdown
            onSelect={handleSelect}
            placeholder={intl.formatMessage({ id: 'userManagement.SELECT_USER' })}
            onChange={setDropdownValue}
            value={dropdownValue}
            removeDuplicatesFrom={latestAddedUsers}
          />
        </Col>
        <Col span={4}>
          <StyledAddUsersButton
            onClick={onAddUser}
            disabled={isAdding}
          >
            {intl.formatMessage({ id: 'userManagement.ADD' })}
          </StyledAddUsersButton>
        </Col>
      </Row>
      {latestAddedUsers?.length > 0 &&
        <>
          <Row>
            <Col span={24}>
              <StyledLabel>
                {intl.formatMessage(
                  { id: 'userManagement.ADD_INFO' },
                  { servicecenter: serviceCenters?.find(s => s.id === activeServiceCenter)?.name },
                )}
              </StyledLabel>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Div marginLeft={10}>
                <Table
                  columns={getColumns()}
                  data={latestAddedUsers}
                  rowKey='id'
                  hasRowSelector={false}
                />
              </Div>
            </Col>
          </Row>
        </>
      }
      <Row>
        <CloseButton onClick={onClose} disabled={isAdding}>
          {intl.formatMessage({ id: 'userManagement.CLOSE' })}
        </CloseButton>
      </Row>
    </StyledModal>
  );
};

AddUsersModal.propTypes = {
  intl: PropTypes.object,
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  handleUpdate: PropTypes.func,
  activeServiceCenter: PropTypes.string,
  roles: PropTypes.array,
  serviceCenters: PropTypes.array,
  handleDelete: PropTypes.func,
  handleAdd: PropTypes.func,
  latestAddedUsers: PropTypes.array,
  isAdding: PropTypes.bool,
  isDeleting: PropTypes.bool,
  isUpdating: PropTypes.bool,
  tooltipText: PropTypes.string,
};

export default injectIntl(AddUsersModal);
